/**
 * StackedBarChart Component
 * Renders one survey question as a compact card: question, favorable score,
 * a 100% stacked bar, and a note when anyone disagreed.
 * Pure CSS flex implementation - no charting library needed.
 * Hover tooltip shows the answer and count. It never shows a name: survey
 * results are anonymous for every role (MED-1345).
 */

import { Tooltip } from '@mantine/core';
import type { ResponseDistribution } from '../../../services/api/feedbackSurveys';
import { SCALE_COLORS, LIGHT_SCALE_INDEXES, EXTENDED_COLORS } from './scaleColors';
import styles from './StackedBarChart.module.css';

/** Likert label map for display */
const LIKERT_LABELS: Record<string, string> = {
  '1': 'Strongly disagree',
  '2': 'Disagree',
  '3': 'Neutral',
  '4': 'Agree',
  '5': 'Strongly agree',
};

interface StackedBarChartProps {
  questionText: string;
  responses: ResponseDistribution[];
  totalResponses: number;
  satisfactionPercentage?: number | null;
}

/** The API sends labels in the form author's casing; show them in sentence case. */
function formatLabel(label: string): string {
  const text = LIKERT_LABELS[label] || label;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

interface NegativeNote {
  index: number;
  text: string;
}

/**
 * Scale questions arrive padded to five positions, worst first, so the first
 * two are the negative answers. Any other shape has no "disagree" to count.
 * QA (2026-10-07): each negative answer keeps its own entry, so "Strongly
 * disagree" and "Disagree" are never added together into one count.
 */
function getNegativeNotes(responses: ResponseDistribution[]): NegativeNote[] {
  if (responses.length !== 5) return [];
  const isSatisfaction = responses[0].label.toLowerCase().includes('dissatisfied');
  const words = isSatisfaction
    ? ['very dissatisfied', 'dissatisfied']
    : ['strongly disagreed', 'disagreed'];
  return [0, 1]
    .filter((index) => responses[index].count > 0)
    .map((index) => ({ index, text: `${responses[index].count} ${words[index]}` }));
}

export function StackedBarChart({
  questionText,
  responses,
  totalResponses,
  satisfactionPercentage,
}: StackedBarChartProps) {
  const isScale = responses.length <= 5;
  const colors = isScale ? SCALE_COLORS : EXTENDED_COLORS;
  const negativeNotes = getNegativeNotes(responses);

  // The bar is one image to assistive tech; the segment numbers are decoration.
  const barLabel = `${responses
    .filter((r) => r.count > 0)
    .map((r) => `${formatLabel(r.label)} ${r.count}`)
    .join(', ')}, of ${totalResponses}`;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.questionLabel}>{questionText}</div>
        {satisfactionPercentage != null && (
          <div
            className={styles.satisfactionValue}
            title="Share of answers that agree or are satisfied"
          >
            {satisfactionPercentage}%
          </div>
        )}
      </div>
      <div className={styles.barInner} role="img" aria-label={barLabel}>
        {responses.map((response, index) => {
          const percentage = totalResponses > 0 ? (response.count / totalResponses) * 100 : 0;
          if (percentage === 0) return null;

          const isNarrow = percentage < 9;

          return (
            <Tooltip.Floating
              key={response.label}
              label={`${formatLabel(response.label)} (${response.count})`}
              color="dark"
            >
              <div
                className={styles.segment}
                data-narrow={isNarrow}
                data-light={isScale && LIGHT_SCALE_INDEXES.has(index)}
                aria-hidden="true"
                style={{
                  flexBasis: `${percentage}%`,
                  backgroundColor: colors[index % colors.length],
                }}
              >
                {!isNarrow && response.count}
              </div>
            </Tooltip.Floating>
          );
        })}
      </div>
      {negativeNotes.length > 0 && (
        <div className={styles.negativeNotes}>
          {negativeNotes.map((note) => (
            <span key={note.index} className={styles.negativeNote}>
              <span
                className={styles.negativeDot}
                style={{ backgroundColor: SCALE_COLORS[note.index] }}
              />
              {note.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default StackedBarChart;
